// Modules
import React, { useState, useEffect } from 'react';

// Utils
import Env from 'Env';
import {
  createJwtToken,
  deleteCreditCard,
  getCreditCard,
  paymentSuccess,
} from 'utils/api/payfabricApi';
import { upsertCustomerWithNewCreditCard } from 'utils/api/customerApi';

// Components
import InvoiceDetails from 'utils/react-library/InvoiceDetails';
import PaymentMethodButtons from 'utils/react-library/PaymentMethodButtons';
import MakePaymentButton from 'utils/react-library/MakePaymentButton';
import PayFabricPaymentsSDK from 'utils/react-library/PayFabricPaymentsSDK';

import {
  Modal,
  ModalTitle,
  ModalBody,
  ModalFooter,
  Button,
} from 'react-bootstrap';
import ModalHeader from 'react-bootstrap/ModalHeader';
import Loading from 'components/Common/Loading';

// Constants
import Environments from 'constants/Environments';

// CSS
import styles from './PaymentLinkMainContent.module.scss';

function PaymentLinkMainContent(props) {
  const {
    total,
    transaction,
    display,
    dispatchDisplay,
    paymentType,
    displayTypes,
    paymentButtons,
    handlePaymentButtonClick,
    handleClosePaymentForm,
    handlePayFabricSuccess,
    handlePayFabricError,
    setCardType,
    setLastFourDigits,
    queryParams,
    handlePayBySavedCard,
  } = props;
  const { showForm } = display;

  const [token, setToken] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showOverwriteSavedCardModal, setShowOverwriteSavedCardModal] =
    useState(false);

  const showMainContent =
    Object.values(display).every((val) => !val) || showForm;
  const transactionId = transaction?.charge?.id;
  const hasPayFabricId = /^pf_/.test(transactionId);

  const handleCancelPayFabricForm = () => {
    handleClosePaymentForm();
    setIsLoading(false);
  };

  const handlePayFabricTransactionCompleted = async (msg) => {
    setIsLoading(true);
    try {
      await paymentSuccess(transactionId, msg);

      if (transaction?.petData?.owner?.customerId) {
        const { practiceId } = transaction;
        const { customerId, paymentMethodId } = transaction?.petData?.owner;
        const { creditCardsInWallet } = await getCreditCard(
          customerId,
          practiceId,
          false
        );
        const isOldCustomerWithNewCreditCard = !!(
          paymentMethodId && creditCardsInWallet > 1
        );
        const isNewCustomer = !paymentMethodId && creditCardsInWallet;

        if (isOldCustomerWithNewCreditCard) {
          setShowOverwriteSavedCardModal(true);
        } else if (isNewCustomer) {
          await overwriteSavedCard();
        } else {
          handlePayFabricSuccess();
        }
      } else {
        handlePayFabricSuccess();
      }

      setIsLoading(false);
    } catch (error) {
      console.log(error);
      setIsLoading(false);
      handlePayFabricError(error.message ?? error);
    }
  };

  const handlePayFabricTransactionFailed = async (msg) => {
    setIsLoading(true);
    handlePayFabricError(msg.errorMessage);
    setIsLoading(false);
  };

  const handleMakePayment = async () => {
    paymentType === 'savedCard'
      ? await handlePayBySavedCard()
      : dispatchDisplay({ type: displayTypes?.showForm });
  };

  const overwriteSavedCard = async () => {
    const { practiceId } = transaction;
    const {
      phone: petOwnerPhone,
      email: petOwnerEmail,
      customerId,
      paymentMethodId,
    } = transaction?.petData?.owner;

    setIsLoading(true);
    await upsertCustomerWithNewCreditCard(
      paymentMethodId && { payment_methods: [{ id: paymentMethodId }] },
      customerId,
      practiceId,
      petOwnerPhone,
      petOwnerEmail,
      { setCardType, setLastFourDigits, queryParams }
    );

    await closeOverwriteModal(false);
  };

  const closeOverwriteModal = async (deleteNewestCreditCard) => {
    const { practiceId } = transaction;
    const { customerId } = transaction?.petData?.owner;

    if (deleteNewestCreditCard) {
      setIsLoading(true);
      await deleteCreditCard(customerId, practiceId, false);
      setIsLoading(false);
    }

    setShowOverwriteSavedCardModal(false);
    handlePayFabricSuccess();
  };

  useEffect(
    () => {
      if (!hasPayFabricId || paymentType !== 'full' || !showForm) return;

      setIsLoading(true);
      getToken();

      async function getToken() {
        const jwtToken = await createJwtToken(transactionId);
        setToken(jwtToken);
      }
    },
    // We need showForm in the dependency array to get a new token
    // if the user clicks the cancel button and then tried to come back
    // Otherwise PayFabric will display an error
    // eslint-disable-next-line
    [hasPayFabricId, transactionId, showForm]
  );

  return (
    <>
      {showMainContent && (
        <InvoiceDetails
          total={total}
          idObj={{ label: 'Invoice #', value: transaction?.invoiceId }}
          lineItems={[
            {
              name: transaction?.charge?.serviceType,
              quantity: '1',
              price: total,
            },
          ]}
          startExpanded={paymentButtons.length > 1 ? false : true}
        />
      )}

      {showMainContent && (
        <PaymentMethodButtons
          clickHandler={handlePaymentButtonClick}
          buttons={paymentButtons}
          selected={paymentType}
        />
      )}

      {hasPayFabricId && paymentType === 'full' && (
        <Modal
          show={showForm}
          onHide={
            showOverwriteSavedCardModal
              ? async () => closeOverwriteModal(true)
              : handleClosePaymentForm
          }
          size='md'
          aria-labelledby='payment-modal'
          centered
        >
          {showOverwriteSavedCardModal && (
            <ModalHeader>
              <ModalTitle>Overwrite Existing Saved Card</ModalTitle>
            </ModalHeader>
          )}

          <ModalBody>
            {isLoading && (
              <div className={styles.formLoading}>
                <Loading />
              </div>
            )}

            {!showOverwriteSavedCardModal && (
              <div className={styles.payFabricHostedForm}>
                {token && (
                  <PayFabricPaymentsSDK
                    token={token}
                    handleFormLoaded={() => setIsLoading(false)}
                    handleCancel={handleCancelPayFabricForm}
                    handleTransactionCompleted={
                      handlePayFabricTransactionCompleted
                    }
                    handleTransactionFailed={handlePayFabricTransactionFailed}
                    isProduction={Env.environment === Environments.Production}
                  />
                )}
              </div>
            )}

            {showOverwriteSavedCardModal && (
              <div className={styles.textContainer}>
                You already have a card saved with us, would you like to
                overwrite your existing saved card?
              </div>
            )}
          </ModalBody>
          {showOverwriteSavedCardModal && (
            <ModalFooter>
              <div className={styles.buttonContainer}>
                <Button
                  variant='light'
                  onClick={async () => closeOverwriteModal(true)}
                  disabled={isLoading}
                >
                  NO
                </Button>
                <Button
                  variant='secondary'
                  onClick={async () => overwriteSavedCard()}
                  disabled={isLoading}
                >
                  YES
                </Button>
              </div>
            </ModalFooter>
          )}
        </Modal>
      )}

      {showMainContent && (
        <MakePaymentButton
          clickHandler={async () => handleMakePayment()}
          isDisabled={showForm}
        />
      )}
    </>
  );
}

export default PaymentLinkMainContent;
